import { useState } from 'react';

import { type ClientPresentationInput, clientPresentationSchema } from '@/entities/session-note';
import { CheckboxGroupList } from '@/shared/ui/checkbox-list/checkbox-group-list';
import { OptionsVisibilityToggle } from '@/shared/ui/checkbox-list/options-visibility-toggle';
import { FieldSection } from '@/shared/ui/forms/field-section';

type ClientPresentationContent = ClientPresentationInput;

const mildLowEnumOptions = clientPresentationSchema.shape.mildLow.unwrap().element.options;
const moderateEnumOptions = clientPresentationSchema.shape.moderate.unwrap().element.options;
const highSevereEnumOptions = clientPresentationSchema.shape.highSevere.unwrap().element.options;

const MILD_LOW_OPTIONS = mildLowEnumOptions.map((label) => ({ value: label, label }));
const MODERATE_OPTIONS = moderateEnumOptions.map((label) => ({ value: label, label }));
const HIGH_SEVERE_OPTIONS = highSevereEnumOptions.map((label) => ({ value: label, label }));

type Props = {
    content: ClientPresentationContent;
    onChange: (content: ClientPresentationContent) => void;
};

export function NessoClientPresentation({ content, onChange }: Props) {
    const [initialDefault] = useState(
        () =>
            (content.mildLow?.length ?? 0) > 0 ||
            (content.moderate?.length ?? 0) > 0 ||
            (content.highSevere?.length ?? 0) > 0 ||
            !!content.mildLowOther ||
            !!content.moderateOther ||
            !!content.highSevereOther,
    );

    return (
        <FieldSection title="Client Presentation" className="gap-9">
            <FieldSection
                label="Clinician observed mood, affect, behavior (check all that apply):"
                className="gap-4"
            >
                <OptionsVisibilityToggle defaultShowOnlySelected={initialDefault}>
                    {({ showOnlySelected }) => (
                        <CheckboxGroupList
                            showOnlySelected={showOnlySelected}
                            groups={[
                                {
                                    label: 'Mild / Low',
                                    options: MILD_LOW_OPTIONS,
                                    values: content.mildLow ?? [],
                                    onValuesChange: (next) =>
                                        onChange({
                                            ...content,
                                            mildLow: next as ClientPresentationContent['mildLow'],
                                        }),
                                    otherValue: content.mildLowOther,
                                    onOtherValueChange: (mildLowOther) =>
                                        onChange({ ...content, mildLowOther }),
                                },
                                {
                                    label: 'Moderate',
                                    options: MODERATE_OPTIONS,
                                    values: content.moderate ?? [],
                                    onValuesChange: (next) =>
                                        onChange({
                                            ...content,
                                            moderate: next as ClientPresentationContent['moderate'],
                                        }),
                                    otherValue: content.moderateOther,
                                    onOtherValueChange: (moderateOther) =>
                                        onChange({ ...content, moderateOther }),
                                },
                                {
                                    label: 'High / Severe',
                                    options: HIGH_SEVERE_OPTIONS,
                                    values: content.highSevere ?? [],
                                    onValuesChange: (next) =>
                                        onChange({
                                            ...content,
                                            highSevere:
                                                next as ClientPresentationContent['highSevere'],
                                        }),
                                    otherValue: content.highSevereOther,
                                    onOtherValueChange: (highSevereOther) =>
                                        onChange({ ...content, highSevereOther }),
                                },
                            ]}
                        />
                    )}
                </OptionsVisibilityToggle>
            </FieldSection>
        </FieldSection>
    );
}
